import { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import Divider from "@mui/material/Divider";
import { FaInfoCircle } from "react-icons/fa";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiInput from "components/VuiInput";
import VuiButton from "components/VuiButton";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import HotelSelector from "components/HotelSelector";
import { Grid, Snackbar } from "@mui/material";
import useCommon from "hooks/useCommon";
import { testIntegrationAPI } from "api/integrations";
import { saveIntegrationAPI } from "api/integrations";
import AlertMessage from "components/AlertMessage/AlertMessage";
import WebhookSuccessDialog from "./WebhookSuccessDialog";
import PasswordInput from "components/PaaswordInput";
import { getHotelIntegrationDetails } from "store/slice/integrationsSlice";
import { clearIntegrationDetails } from "store/slice/integrationsSlice";
import OverlayLoader from "components/OverlayLoader";
import dayjs from "dayjs";

const ConnectIntegrationDialog = ({ open, onClose, integration, values, onChange }) => {
  if (!integration) return null;

  const { DISPATCH, NAVIFATE, STORE } = useCommon();
  const { hotelId } = STORE((state) => state.ui);
  const { integrationDetails, integrationDetailsLoading } = STORE((state) => state.integrations);

  const [webhookDialogOpen, setWebhookDialogOpen] = useState(false);
  const [createdWebhook, setCreatedWebhook] = useState(null);

  const [formValues, setFormValues] = useState({});
  const [syncMode, setSyncMode] = useState("webhook"); // webhooks | polling | both
  const [useManualWebhook, setUseManualWebhook] = useState(false);
  const [pollingInterval, setPollingInterval] = useState("5");
  const [testing, setTesting] = useState(false);
  const [testPassed, setTestPassed] = useState(false);
  const [saving, setSaving] = useState(false);

  const [statusMessage, setStatusMessage] = useState({
    type: null, // "success" | "error"
    message: "",
  });

  const showStatus = (message, type = "success") => {
    setStatusMessage({ message, type });
  };

  const clearStatus = () => {
    setStatusMessage({ message: "", type: null });
  };

  const resolveErrorMessage = (error, fallback) => {
    return (
      error?.response?.data?.message || error?.response?.data?.error || error?.message || fallback
    );
  };

  const handleChange = (key, value) => {
    setFormValues((prev) => ({
      ...prev,
      [key]: value,
    }));

    setTestPassed(false);
  };

  const handleSyncModeChange = (value) => {
    setSyncMode(value);
    setTestPassed(false);
  };

  const buildIntegrationPayload = () => {
    const payload = {
      providerKey: integration.key,

      environment: formValues.environment || "demo",

      credentials: {},
      mappingValues: {},

      syncMode: syncMode,
      pollingIntervalMinutes:
        syncMode === "polling" || syncMode === "both" ? Number(pollingInterval) : 1,

      useManualWebhook: false,
      manualWebhookUrl: null,
      manualWebhookSecret: null,
    };

    /* -------- Credentials -------- */
    integration.credentialFields?.forEach((field) => {
      payload.credentials[field.name] = formValues[field.name] || "";
    });

    /* -------- Mapping -------- */
    integration.mappingFields?.forEach((field) => {
      payload.mappingValues[field.name] = formValues[field.name] || "";
    });

    /* -------- Webhook -------- */
    if (syncMode === "webhook" || syncMode === "both") {
      payload.useManualWebhook = useManualWebhook;

      if (useManualWebhook) {
        payload.manualWebhookUrl = formValues.manualWebhookUrl || "";
        payload.manualWebhookSecret = formValues.manualWebhookSecret || "";
      }
    }

    return payload;
  };

  // const handleTestConnection = async () => {
  //   const formData = buildIntegrationPayload();

  //   // await dispatch(testIntegration(formData));
  //   console.log("TEST payload ====>", formData);
  // };

  const handleTestConnection = async () => {
    try {
      setTesting(true);

      const payload = buildIntegrationPayload();

      await testIntegrationAPI({
        hotelId,
        data: payload,
      });

      setTestPassed(true); // allow saving

      showStatus("Connection test successful", "success");
    } catch (error) {
      showStatus(resolveErrorMessage(error, "Connection test failed"), "error");
    } finally {
      setTesting(false);
    }
  };

  const handleSaveIntegration = async () => {
    try {
      setSaving(true);

      const payload = buildIntegrationPayload();

      const response = await saveIntegrationAPI({
        hotelId,
        data: payload,
      });

      const integrationData = response?.data?.integration;

      // Extract webhook
      if (integrationData?.webhook?.url) {
        setWebhookDialogOpen(true);
        setCreatedWebhook(integrationData.webhook);
        // onClose();
      }

      // onClose(); // close connection modal
    } catch (error) {
      showStatus(resolveErrorMessage(error, "Failed to save integration"), "error");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!open) return;

    if (integration.supportsWebhooks && integration.supportsPolling) {
      setSyncMode("webhook");
    } else if (integration.supportsPolling) {
      setSyncMode("polling");
    } else {
      setSyncMode("webhook");
    }

    setUseManualWebhook(false);
    setPollingInterval(15);
    setTestPassed(false);
  }, [open, integration]);

  useEffect(() => {
    if (!open || !hotelId || !integration?.key) return;

    // only fetch if already connected
    if (!integration?.connected) return;

    DISPATCH(
      getHotelIntegrationDetails({
        hotelId,
        providerKey: integration.key,
      })
    );

    return () => {
      DISPATCH(clearIntegrationDetails());
    };
  }, [open, hotelId, integration]);

  useEffect(() => {
    if (!integrationDetails) return;

    // -------- Credentials --------
    const credentials = integrationDetails.credentials || {};

    // -------- Mapping --------
    const mappingValues = integrationDetails.mappingValues || {};

    // -------- Merge All Form Values --------
    setFormValues({
      environment: integrationDetails.environment || "demo",

      ...credentials,
      ...mappingValues,

      manualWebhookUrl: integrationDetails?.webhook?.url || "",
      manualWebhookSecret: integrationDetails?.webhook?.secret || "",
    });

    // -------- Sync Config --------
    setSyncMode(integrationDetails?.syncConfig?.mode || "webhook");

    setPollingInterval(integrationDetails?.syncConfig?.pollingIntervalMinutes || 15);

    // setTestPassed(true);
  }, [integrationDetails]);

  const generateWebhookSecret = () => {
    const secret = `whsec_${crypto.randomUUID().replaceAll("-", "")}`;
    handleChange("manualWebhookSecret", secret);
  };

  return (
    <>
      <Dialog
        open={open}
        maxWidth="md"
        fullWidth
        onClose={(e, reason) => {
          if (reason === "backdropClick") return;

          DISPATCH(clearIntegrationDetails());

          onClose();
        }}
      >
        <OverlayLoader open={integrationDetailsLoading} message="Loading integration details..." />
        <VuiBox p={3}>
          <VuiBox display="flex" justifyContent="space-between" alignItems="center">
            <VuiTypography variant="lg">Configure {integration.displayName}</VuiTypography>

            <VuiButton
              variant="text"
              onClick={() => {
                DISPATCH(clearIntegrationDetails());
                onClose();
              }}
            >
              ✕
            </VuiButton>
          </VuiBox>

          <Divider sx={{ my: 1.5 }} />

          {integration.instructions && (
            <VuiBox display="flex" gap={1} mb={2}>
              <FaInfoCircle size={14} />
              <VuiTypography variant="caption" opacity={0.75}>
                {integration.instructions}
              </VuiTypography>
            </VuiBox>
          )}
          {/* ================= Hotel + Environment ================= */}
          <Grid container spacing={2} alignItems="flex-start" mb={2}>
            <Grid item xs={12} md={6}>
              <VuiTypography variant="button" mb={2}>
                Select Property
              </VuiTypography>

              <HotelSelector minWidth={210} multiple={false} />
            </Grid>

            <Grid item xs={12} md={6}>
              <VuiTypography variant="button" mb={1} textAlign={{ xs: "left", md: "center" }}>
                Environment
              </VuiTypography>

              <RadioGroup
                row
                value={formValues.environment || "demo"}
                onChange={(e) => handleChange("environment", e.target.value)}
                sx={{
                  display: "flex",
                  justifyContent: "space-evenly",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <FormControlLabel
                  value="demo"
                  control={<Radio />}
                  label={<VuiTypography variant="caption">Demo / Sandbox</VuiTypography>}
                  sx={{ margin: 0 }}
                />

                <FormControlLabel
                  value="live"
                  control={<Radio />}
                  label={<VuiTypography variant="caption">Live</VuiTypography>}
                  sx={{ margin: 0 }}
                />
              </RadioGroup>
            </Grid>
          </Grid>

          {/* ================= Credentials ================= */}
          {integration.credentialFields?.length > 0 && (
            <>
              <VuiTypography variant="button" mb={0}>
                Credentials
              </VuiTypography>

              {integration.credentialFields.map((field) => (
                <VuiBox key={field.name} mb={0}>
                  <VuiTypography variant="caption" mb={0.5} display="block">
                    {field.label}
                    {field.required && (
                      <VuiTypography component="span" color="error">
                        {" "}
                        *
                      </VuiTypography>
                    )}
                  </VuiTypography>

                  {field.type === "password" ? (
                    <PasswordInput
                      name={field.name}
                      value={formValues[field.name] || ""}
                      onChange={(e) => handleChange(field.name, e.target.value)}
                      placeholder={field.placeholder}
                      fullWidth
                      gradient
                    />
                  ) : (
                    <VuiInput
                      fullWidth
                      gradient
                      type={field.type || "text"}
                      value={formValues[field.name] || ""}
                      onChange={(e) => handleChange(field.name, e.target.value)}
                      placeholder={field.placeholder}
                      inputProps={{ maxLength: 30 }}
                    />
                  )}

                  {field.helpText && (
                    <VuiTypography variant="caption" opacity={0.6}>
                      {field.helpText}
                    </VuiTypography>
                  )}
                </VuiBox>
              ))}
            </>
          )}

          {/* ================= Mapping Fields ================= */}
          {integration.mappingFields?.length > 0 && (
            <>
              <VuiTypography variant="button" mt={2} mb={1}>
                Mapping Feilds
              </VuiTypography>

              {integration.mappingFields.map((field) => (
                <VuiBox key={field.name} mb={1.5}>
                  <VuiTypography variant="caption" mb={0.5} display="block">
                    {field.label}
                    {field.required && (
                      <VuiTypography component="span" color="error">
                        {" "}
                        *
                      </VuiTypography>
                    )}
                  </VuiTypography>

                  {field.type === "password" ? (
                    <PasswordInput
                      name={field.name}
                      value={formValues[field.name] || ""}
                      onChange={(e) => handleChange(field.name, e.target.value)}
                      placeholder={field.placeholder}
                      fullWidth
                      gradient
                    />
                  ) : (
                    <VuiInput
                      fullWidth
                      gradient
                      value={formValues[field.name] || ""}
                      onChange={(e) => handleChange(field.name, e.target.value)}
                      placeholder={field.placeholder}
                      inputProps={{ maxLength: 500 }}
                    />
                  )}

                  {field.helpText && (
                    <VuiTypography variant="caption" opacity={0.6}>
                      {field.helpText}
                    </VuiTypography>
                  )}
                </VuiBox>
              ))}
            </>
          )}

          {/* ================= Sync Mode ================= */}
          {(integration.supportsWebhooks || integration.supportsPolling) && (
            <>
              <VuiTypography variant="button" mb={1} textAlign="center">
                Sync Mode
              </VuiTypography>
              <RadioGroup
                row
                value={syncMode}
                onChange={(e) => handleSyncModeChange(e.target.value)}
                sx={{
                  display: "flex",
                  justifyContent: "space-evenly",
                  alignItems: "center",
                  width: "100%",
                  mt: 1,
                }}
              >
                {integration.supportsWebhooks && (
                  <FormControlLabel
                    value="webhook"
                    control={<Radio />}
                    label={<VuiTypography variant="caption">Webhook</VuiTypography>}
                    sx={{ margin: 0 }}
                  />
                )}

                {integration.supportsPolling && (
                  <FormControlLabel
                    value="polling"
                    control={<Radio />}
                    label={<VuiTypography variant="caption">Polling</VuiTypography>}
                    sx={{ margin: 0 }}
                  />
                )}

                {integration.supportsWebhooks && integration.supportsPolling && (
                  <FormControlLabel
                    value="both"
                    control={<Radio />}
                    label={<VuiTypography variant="caption">Both</VuiTypography>}
                    sx={{ margin: 0 }}
                  />
                )}
              </RadioGroup>
            </>
          )}

          {/* ================= Webhook Config ================= */}
          {(syncMode === "webhook" || syncMode === "both") && integration.supportsWebhooks && (
            <>
              <Divider sx={{ mt: 0 }} />

              <VuiBox my={1}>
                <VuiTypography
                  variant="body2"
                  opacity={0.7}
                  sx={{
                    fontSize: "12px",
                    wordSpacing: "0.1em",
                  }}
                >
                  By default, webhook URL and secret are generated automatically after you activate
                  the integration. You can switch to manual configuration if needed.
                </VuiTypography>
              </VuiBox>

              {/* ===== Manual Toggle ===== */}
              {/* <VuiBox mb={1} ml={1}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={useManualWebhook}
                      onChange={() => setUseManualWebhook(!useManualWebhook)}
                      sx={{
                        color: "rgba(255,255,255,0.6)",
                        "&.Mui-checked": {
                          color: "#4fd1c5", // Vision UI info color
                        },
                      }}
                    />
                  }
                  label={
                    <VuiTypography variant="caption">
                      Use manual webhook configuration
                    </VuiTypography>
                  }
                />
              </VuiBox> */}

              {/* ===== Manual Webhook Config ===== */}
              {/* {useManualWebhook && (
                <>
                  <VuiBox mb={1.5}>
                    <VuiTypography variant="caption">
                      Manual Webhook URL *
                    </VuiTypography>
                    <VuiInput
                      fullWidth
                      gradient
                      value={formValues.manualWebhookUrl || ""}
                      onChange={(e) => handleChange("manualWebhookUrl", e.target.value)}
                      placeholder="https://example.com/webhook"
                    />
                  </VuiBox>

                  <VuiBox mb={1.5}>
                    <VuiTypography variant="caption">
                      Manual Webhook Secret
                    </VuiTypography>

                    <VuiBox display="flex" gap={1}>
                      <VuiInput
                        fullWidth
                        gradient
                        value={formValues.manualWebhookSecret || ""}
                        onChange={(e) => handleChange("manualWebhookSecret", e.target.value)}
                        placeholder="Leave empty to auto-generate"
                      />
                      <VuiButton variant="outlined" onClick={generateWebhookSecret}>
                        Generate
                      </VuiButton>
                    </VuiBox>
                  </VuiBox>
                </>
              )} */}
            </>
          )}

          {/* ================= Polling Config ================= */}
          {(syncMode === "polling" || syncMode === "both") && integration.supportsPolling && (
            <>
              <Divider sx={{ my: 0 }} />

              <VuiTypography variant="button" mb={1}>
                Polling Configuration
              </VuiTypography>

              <VuiBox width={220}>
                <VuiTypography variant="caption">Polling Interval (minutes)</VuiTypography>
                <VuiInput
                  type="number"
                  gradient
                  value={pollingInterval}
                  onChange={(e) => {
                    const value = e.target.value;

                    // allow empty while typing
                    if (value === "") {
                      setPollingInterval("");
                      return;
                    }

                    // only numbers
                    if (!/^\d+$/.test(value)) return;

                    // remove leading zeros
                    const normalized = String(parseInt(value, 10));

                    setPollingInterval(normalized);
                  }}
                  inputProps={{ min: 5, step: 5 }}
                  sx={{
                    // Chrome, Safari, Edge
                    "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                      WebkitAppearance: "none",
                      margin: 0,
                    },
                    // Firefox
                    "& input[type=number]": {
                      MozAppearance: "textfield",
                    },
                  }}
                />
              </VuiBox>
            </>
          )}
          {/* ================= Existing Integration Info ================= */}
          {integrationDetails && (
            <>
              <Divider sx={{ my: 1 }} />

              <VuiBox
                p={2}
                borderRadius="20px"
                sx={{
                  background:
                    "linear-gradient(135deg, rgba(255,255,255,0.06), rgba(255,255,255,0.03))",
                  border: "1px solid rgba(255,255,255,0.08)",
                  backdropFilter: "blur(10px)",
                }}
              >
                {/* Header */}
                <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <VuiTypography variant="button">Integration Status</VuiTypography>

                  <VuiBox
                    px={1}
                    py={0}
                    borderRadius="12px"
                    // sx={{
                    //   background:
                    //     integrationDetails?.status === "active"
                    //       ? "rgba(34,197,94,0.15)"
                    //       : "rgba(245,158,11,0.15)",
                    //   border:
                    //     integrationDetails?.status === "active"
                    //       ? "1px solid rgba(34,197,94,0.35)"
                    //       : "1px solid rgba(245,158,11,0.35)",
                    // }}
                  >
                    <VuiTypography
                      variant="caption"
                      color={integrationDetails?.status === "active" ? "success" : "warning"}
                      fontWeight="bold"
                      textTransform="capitalize"
                    >
                      {integrationDetails?.status}
                    </VuiTypography>
                  </VuiBox>
                </VuiBox>

                {/* Webhook URL */}
                {integrationDetails?.webhook?.url && (
                  <VuiBox mb={2}>
                    <VuiTypography variant="caption" opacity={0.7} mb={0.5} display="block">
                      Webhook URL
                    </VuiTypography>

                    <VuiBox
                      p={1}
                      borderRadius="14px"
                      sx={{
                        background: "rgba(255,255,255,0.03)",
                        border: "1px solid rgba(255,255,255,0.06)",
                        wordBreak: "break-all",
                      }}
                    >
                      <VuiTypography variant="caption" sx={{ fontFamily: "monospace" }}>
                        {integrationDetails.webhook.url}
                      </VuiTypography>
                    </VuiBox>
                  </VuiBox>
                )}

                {/* Webhook Secret */}
                {integrationDetails?.webhook?.secret && (
                  <VuiBox mb={2}>
                    <VuiTypography variant="caption" opacity={0.7} mb={0.5} display="block">
                      Webhook Secret
                    </VuiTypography>

                    <PasswordInput
                      value={integrationDetails.webhook.secret}
                      fullWidth
                      gradient
                      disabled
                    />
                  </VuiBox>
                )}

                {/* Sync Details */}
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <VuiTypography variant="caption" opacity={0.7}>
                      Last Webhook
                    </VuiTypography>

                    <VuiTypography variant="caption" display="block">
                      {integrationDetails?.syncConfig?.lastWebhookAt
                        ? dayjs(integrationDetails.syncConfig.lastWebhookAt).format(
                            "MMM D, YYYY • h:mm A"
                          )
                        : "Never"}
                    </VuiTypography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <VuiTypography variant="caption" opacity={0.7}>
                      Last Successful Sync
                    </VuiTypography>

                    <VuiTypography variant="caption" display="block">
                      {integrationDetails?.syncConfig?.lastSuccessfulProcessAt
                        ? dayjs(integrationDetails.syncConfig.lastSuccessfulProcessAt).format(
                            "MMM D, YYYY • h:mm A"
                          )
                        : "Never"}
                    </VuiTypography>
                  </Grid>
                </Grid>
              </VuiBox>
            </>
          )}
          {/* ================= Actions ================= */}
          <Divider sx={{ my: 1 }} />

          <VuiBox>
            <AlertMessage
              message={statusMessage.message}
              color={statusMessage.type}
              onClose={clearStatus}
            />

            <VuiBox display="flex" justifyContent="flex-end" gap={2} mt={2}>
              <VuiButton
                variant="outlined"
                color="primary"
                onClick={handleTestConnection}
                disabled={testing || saving}
              >
                {testing ? "Testing..." : "Test Connection"}
              </VuiButton>

              {testPassed && (
                <VuiButton
                  color="primary"
                  onClick={handleSaveIntegration}
                  disabled={saving || testing}
                >
                  {saving ? "Saving..." : "Save & Activate"}
                </VuiButton>
              )}

              {/* <VuiButton
                variant="text"
                color="warning"
                onClick={() => {
                  setCreatedWebhook({
                    url: "https://api.roomkeysociety.com/webhooks/test-provider",
                    secret: "whsec_test_secret_123",
                  });
                  setWebhookDialogOpen(true);
                }}
              >
                Open Webhook Dialog (Test)
              </VuiButton> */}
            </VuiBox>
          </VuiBox>
        </VuiBox>
      </Dialog>

      <WebhookSuccessDialog
        open={webhookDialogOpen}
        webhook={createdWebhook}
        onClose={() => {
          setWebhookDialogOpen(false);
          setCreatedWebhook(null);
        }}
      />
    </>
  );
};

export default ConnectIntegrationDialog;
